import { getCreatorSlugFromHost } from "./getCreatorSlugFromHost";

export function isWebsiteAliasPath(pathname: string): boolean {
  return /(^|\/)website(?:\/|$)/i.test(String(pathname || ""));
}

function spaBasePath(pathname: string, isEmbedComponent?: boolean): string {
  if (pathname.includes("/embed") && !isEmbedComponent) return "/embed/";
  if (pathname.includes("/preview")) return "/preview/";
  if (pathname.includes("/creator")) return "/creator/";
  // Public demo / draft preview lives at `/website/:user` — keep that prefix
  // so nested pages stay on WebsiteTemplatePage (not LiB `/:user/...`).
  if (isWebsiteAliasPath(pathname)) return "/website/";
  return "/";
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    return segment.toLowerCase();
  }
}

/**
 * Path to pass to `navigate()`.
 * On custom domains / creator subdomains, drops `/:user` (and a leftover
 * `/website` segment). On platform hosts, keeps `/website/:user/...` so demo
 * and draft preview nested routes stay under the website alias.
 */
export function toCreatorBrowserPathname(
  pathname: string,
  username?: string,
): string {
  let p = String(pathname || "/").trim() || "/";
  if (!p.startsWith("/")) p = `/${p}`;
  const segments = p.split("/").filter(Boolean);
  const omitUser = creatorPublicPathOmitsUsernamePrefix();
  // Only strip `/website` when the host already omits the username prefix
  // (custom domain / creator subdomain). On browsable.ai / localhost, nested
  // demo navigation must remain `/website/:user/shop` etc.
  if (omitUser && segments[0] && decodePathSegment(segments[0]) === "website") {
    segments.shift();
  }
  const user = String(username || "").trim().toLowerCase();
  if (omitUser) {
    if (user && segments[0] && decodePathSegment(segments[0]) === user) {
      segments.shift();
    }
  }
  return segments.length ? `/${segments.join("/")}` : "/";
}

/**
 * When true, public SPA paths should not include `/:username` (custom domain, mapped host,
 * Browsable creator subdomain, or `{slug}.localhost`). Matches `Routes` spoofing + share URLs.
 */
export function creatorPublicPathOmitsUsernamePrefix(hostname?: string): boolean {
  const host = String(
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : ""),
  ).toLowerCase();

  if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return false;

  if (host.endsWith(".localhost") && host !== "localhost") return true;

  if (getCreatorSlugFromHost(host)) return true;

  const isPlatformApex =
    host === "browsable.ai" ||
    host === "www.browsable.ai" ||
    host === "browsable.bio" ||
    host === "www.browsable.bio" ||
    host.includes("searchbubble.com") ||
    host.endsWith(".vercel.app");
  if (isPlatformApex) return false;
  if (host.endsWith(".browsable.ai") || host.endsWith(".browsable.bio")) return true;

  return true;
}

export function isWebsiteTemplateLinkInBioPath(pathname: string): boolean {
  return /\/link-in-bio(?:\/|$)/.test(pathname);
}

/** Top-level `/website/:user/*` segments — not stayuptodate component slugs under link-in-bio. */
const WEBSITE_TEMPLATE_ROUTE_SEGMENTS = new Set([
  "about",
  "contact",
  // LiB Contact Me path — alias of website `/contact` (see parsePublicWebsiteSubpath).
  "contact-me",
  "press",
  "newsletter",
  "search",
  "shop",
  "content",
  "blog",
  "platform",
  "link-in-bio",
]);

export function isKnownWebsiteTemplateRoute(route: string): boolean {
  const trimmed = String(route || "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  if (!trimmed) return false;
  const first = trimmed.split("/")[0]?.toLowerCase() ?? "";
  return WEBSITE_TEMPLATE_ROUTE_SEGMENTS.has(first);
}

/** Public path for copy/open. Demo uses `/website/:user` so unpublished LiB pages resolve. */
export function buildCreatorPagePath(args: {
  username: string;
  route?: string;
  demo?: boolean;
}): string {
  const user = String(args.username || "").trim().toLowerCase();
  if (!user) return "";
  const rest = String(args.route || "").replace(/^\/+/, "").replace(/\/+$/, "");
  return getCreatorSpaRelativePath({
    username: user,
    route: rest || undefined,
    pathname: args.demo ? `/website/${user}` : `/${user}`,
  });
}

/**
 * Relative path for React Router `navigate()`, aligned with `getCreatorPublicBaseUrl` /
 * custom-domain behavior (same rules as legacy `getHomePath` / `getPublicUrlWithUsername`).
 */
export function getCreatorSpaRelativePath(args: {
  username: string;
  /** Route segment without leading slash (e.g. component id, `contact-me`). Omit for home. */
  route?: string;
  pathname?: string;
  isEmbedComponent?:boolean;
}): string {
  const pathname =
    args.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const base = spaBasePath(pathname, args.isEmbedComponent);
  const u = String(args.username || "").trim();
  const route = String(args.route || "").replace(/^\/+/, "").replace(/\/+$/, "");

  let result: string;
  if (creatorPublicPathOmitsUsernamePrefix()) {
    result = route ? `${base}${route}` : base;
  } else if (!route) {
    result = `${base}${u}`;
  } else if (isWebsiteTemplateLinkInBioPath(pathname)) {
    result = isKnownWebsiteTemplateRoute(route)
      ? `${base}${u}/${route}`
      : `${base}${u}/link-in-bio/${route}`;
  } else {
    result = `${base}${u}/${route}`;
  }

  return toCreatorBrowserPathname(result, u);
}
